import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, Eye, EyeOff, ExternalLink, ArrowRight } from 'lucide-react';
import HomeSectionsEditor from './HomeSectionsEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Home Page Preview Component
const HomePreview = ({ heroContent }) => (
  <div className="space-y-4">
    {/* Hero Preview */}
    <div className="relative bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 rounded-xl overflow-hidden">
      {heroContent.images && heroContent.images.length > 0 && (
        <div className="absolute inset-0 opacity-30">
          <img 
            src={heroContent.images[0]} 
            alt="Hero background" 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="relative py-8 px-4 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          {heroContent.title} <span className="text-orange-500">{heroContent.highlight}</span>
        </h1>
        <p className="text-sm text-gray-700 max-w-lg mx-auto mb-4">
          {heroContent.description}
        </p>
        <div className="flex justify-center gap-3">
          <span className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold">
            {heroContent.button_primary}
          </span>
          <span className="inline-flex items-center px-4 py-2 bg-white border text-gray-700 rounded-lg text-sm font-medium">
            {heroContent.button_secondary}
          </span>
        </div>
      </div>
    </div>

    {/* Image Gallery Preview */}
    {heroContent.images && heroContent.images.length > 0 && (
      <div className="bg-white p-3 rounded-xl border">
        <p className="text-xs text-gray-500 mb-2">Slideshow Images ({heroContent.images.length})</p>
        <div className="flex gap-2 overflow-x-auto">
          {heroContent.images.slice(0, 4).map((img, idx) => (
            <img key={idx} src={img} alt={`Slide ${idx + 1}`} className="w-16 h-12 object-cover rounded" />
          ))}
          {heroContent.images.length > 4 && (
            <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500">
              +{heroContent.images.length - 4}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

const HomeContentEditor = () => {
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroContent, setHeroContent] = useState({
    title: 'Empowering Public Employees of',
    highlight: 'Papua New Guinea',
    description: 'Join us in our mission to advocate for better working conditions, fair treatment, and professional development opportunities for all public sector employees across PNG.',
    button_primary: 'Become a Member',
    button_secondary: 'Learn More',
    image_mode: 'slideshow', // 'slideshow' | 'static' | 'gradient'
    images: [],
    background_image: '', // optional legacy/static field
    slideshow_duration: 5000 // milliseconds
  });


  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      
      response.data.forEach(item => {
        if (item.section_name === 'hero') {
          setHeroContent(item.content);
        }
      });
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }

  const normalizeGoogleDriveImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';

    if (url.includes('lh3.googleusercontent.com/d/')) return url;

    const m1 = url.match(/drive\.google\.com\/file\/d\/([^/]+)\//);
    if (m1 && m1[1]) return `https://lh3.googleusercontent.com/d/${m1[1]}=s2000`;

    const m2 = url.match(/[?&]id=([^&]+)/);
    if (url.includes('drive.google.com/open') && m2 && m2[1]) {
      return `https://lh3.googleusercontent.com/d/${m2[1]}=s2000`;
    }

    return url;
  };

  };

  const handleHeroChange = (field, value) => {
    if (field === 'background_image') {
      setHeroContent({ ...heroContent, background_image: normalizeGoogleDriveImageUrl(value) });
      return;
    }

    setHeroContent({ ...heroContent, [field]: value });
  };

  const handleAddImage = () => {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      const normalized = normalizeGoogleDriveImageUrl(imageUrl);
      setHeroContent({
        ...heroContent,
        images: [...(heroContent.images || []), normalized],
      });
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...(heroContent.images || [])];
    newImages.splice(index, 1);
    setHeroContent({ ...heroContent, images: newImages });
  };



  const handleSaveHero = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/page-content/home/hero`,
        { content: heroContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hero section updated successfully');
    } catch (error) {
      console.error('Error saving hero:', error);
      toast.error('Failed to save hero section');
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Preview Panel */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Button
            type="button"
            variant={showPreview ? "default" : "outline"}
            onClick={() => setShowPreview(!showPreview)}
            className={showPreview ? "bg-blue-500 hover:bg-blue-600" : ""}
            data-testid="toggle-preview-btn"
          >
            {showPreview ? <EyeOff className="mr-2" size={18} /> : <Eye className="mr-2" size={18} />}
            {showPreview ? 'Hide Preview' : 'Show Live Preview'}
          </Button>
          
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600"
          >
            <ExternalLink size={16} className="mr-1" />
            Open in New Tab
          </a>
        </div>

        {showPreview && (
          <div className="border-2 border-blue-200 rounded-xl p-4 bg-gray-50 mb-6">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-100">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
              <span className="text-xs text-gray-500 ml-2">Live Preview</span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <HomePreview heroContent={heroContent} />
            </div>
          </div>
        )}
      </div>

      {/* Hero Section Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Hero Section</CardTitle>
          <CardDescription>Edit the main banner content on the homepage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="hero-title">Main Title</Label>
            <Input
              id="hero-title"
              data-testid="input-hero-title"
              value={heroContent.title}
              onChange={(e) => handleHeroChange('title', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="hero-highlight">Highlighted Text (Orange)</Label>
            <Input
              id="hero-highlight"
              data-testid="input-hero-highlight"
              value={heroContent.highlight}
              onChange={(e) => handleHeroChange('highlight', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="hero-description">Description</Label>
            <Textarea
              id="hero-description"
              data-testid="input-hero-description"
              value={heroContent.description}
              onChange={(e) => handleHeroChange('description', e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hero-btn-primary">Primary Button Text</Label>
              <Input
                id="hero-btn-primary"
                data-testid="input-hero-btn-primary"
                value={heroContent.button_primary}
                onChange={(e) => handleHeroChange('button_primary', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="hero-btn-secondary">Secondary Button Text</Label>
              <Input
                id="hero-btn-secondary"
                data-testid="input-hero-btn-secondary"
                value={heroContent.button_secondary}
                onChange={(e) => handleHeroChange('button_secondary', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Hero Images Section */}
          <div className="pt-6 border-t">
            <h4 className="text-lg font-semibold mb-4">Hero Background Images</h4>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="image-mode">Display Mode</Label>
                <select
                  id="image-mode"
                  data-testid="select-image-mode"
                  value={heroContent.image_mode || 'slideshow'}
                  onChange={(e) => handleHeroChange('image_mode', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md mt-1"
                >
                  <option value="slideshow">Slideshow (Multiple Images)</option>

                  <div className="mt-6">
                    <Label htmlFor="hero-bg-image">Background Image URL (optional)</Label>
                    <Input
                      id="hero-bg-image"
                      data-testid="input-hero-background-image"
                      value={heroContent.background_image || ''}
                      onChange={(e) => handleHeroChange('background_image', e.target.value)}
                      className="mt-1"
                      placeholder="Paste a direct image URL or a Google Drive share link"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Tip: If you paste a Google Drive link like
                      {' '}<span className="font-mono">drive.google.com/file/d/…/view</span>,
                      it will be converted automatically.
                    </p>
                  </div>

                  <option value="static">Static (Single Image)</option>
                  <option value="gradient">Gradient Background (No Image)</option>
                </select>
              </div>

              {(heroContent.image_mode === 'slideshow' || heroContent.image_mode === 'static') && (
                <>
                  {heroContent.image_mode === 'slideshow' && (
                    <div>
                      <Label htmlFor="slideshow-duration">Slideshow Duration (seconds)</Label>
                      <Input
                        id="slideshow-duration"
                        type="number"
                        data-testid="input-slideshow-duration"
                        value={(heroContent.slideshow_duration || 5000) / 1000}
                        onChange={(e) => handleHeroChange('slideshow_duration', parseInt(e.target.value) * 1000)}
                        className="mt-1"
                        min="3"
                        max="10"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Images</Label>
                    <div className="mt-2 space-y-2">
                      {(heroContent.images || []).map((image, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <img src={image} alt={`Hero ${index + 1}`} className="w-20 h-12 object-cover rounded" />
                          <input
                            type="text"
                            value={image}
                            onChange={(e) => {
                              const newImages = [...(heroContent.images || [])];
                              newImages[index] = e.target.value;
                              handleHeroChange('images', newImages);
                            }}
                            className="flex-1 px-2 py-1 border rounded text-sm"
                            placeholder="Image URL"
                          />
                          <button
                            onClick={() => handleRemoveImage(index)}
                            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      
                      {heroContent.image_mode === 'static' && (heroContent.images || []).length === 0 && (
                        <p className="text-sm text-gray-500">No image added yet</p>
                      )}
                      
                      {heroContent.image_mode === 'slideshow' && (
                        <p className="text-sm text-gray-500">
                          {(heroContent.images || []).length} image(s) added
                        </p>
                      )}
                    </div>
                    
                    {(heroContent.image_mode === 'static' && (heroContent.images || []).length < 1) && (
                      <Button
                        type="button"
                        onClick={handleAddImage}
                        className="mt-2 w-full bg-blue-500 hover:bg-blue-600"
                      >
                        Add Image
                      </Button>
                    )}
                    
                    {heroContent.image_mode === 'slideshow' && (
                      <Button
                        type="button"
                        onClick={handleAddImage}
                        className="mt-2 w-full bg-blue-500 hover:bg-blue-600"
                      >
                        Add Image to Slideshow
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <Button
            onClick={handleSaveHero}
            data-testid="save-hero-btn"
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} />
            Save Hero Section
          </Button>
        </CardContent>
      </Card>

      <HomeSectionsEditor />
    </div>
  );
};

export default HomeContentEditor;
